
/* ========================================================================== */
/*  FILE: components/home/TestimonialsClient.tsx                               */
/* ========================================================================== */

"use client";

import Image from "next/image";
import React, { useMemo } from "react";
import { Quote, Star, TrendingUp, Users } from "lucide-react";

type Testimonial = {
  content: string;
  author: string;
  role: string;
  company?: string;
  rating: number; // 1..5
  avatar: string;
};

export type TestimonialsClientProps = {
  heading?: string;
  subheading?: string;
  testimonials?: Testimonial[];
  showStats?: boolean;
  stats?: Array<{ label: string; value: string; icon?: React.ComponentType<{ className?: string }> }>;
};

function cn(...classes: Array<string | undefined | null | false>) {
  return classes.filter(Boolean).join(" ");
}

function clampRating(r: number) {
  if (!Number.isFinite(r)) return 5;
  return Math.max(1, Math.min(5, Math.round(r)));
}

export default function TestimonialsClient(props: TestimonialsClientProps) {
  const testimonials: Testimonial[] = useMemo(
    () =>
      props.testimonials ?? [
        {
          content:
            "IndeMenu has revolutionized how we manage our restaurant. Orders increased by 35% in the first month!",
          author: "Sarah Gondwe",
          role: "Restaurant Owner",
          company: "The Capital Grill",
          rating: 5,
          avatar:
            "https://images.unsplash.com/photo-1494790108755-2616b612b786?q=80&w=260&auto=format&fit=crop",
        },
        {
          content:
            "As a frequent diner, I love booking tables and tracking orders. The QR ordering is genuinely brilliant.",
          author: "James Phiri",
          role: "Tech Entrepreneur",
          company: "Blantyre Tech Hub",
          rating: 5,
          avatar:
            "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=260&auto=format&fit=crop",
        },
        {
          content:
            "Our waitstaff is faster and customers love the seamless experience. The menu updates are a huge win.",
          author: "Grace Mwale",
          role: "Restaurant Manager",
          company: "Lakeview Bistro",
          rating: 5,
          avatar:
            "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=260&auto=format&fit=crop",
        },
      ],
    [props.testimonials]
  );

  const heading = props.heading ?? "Loved by Diners & Restaurants";
  const subheading =
    props.subheading ??
    "Join thousands of users transforming the dining experience with fast discovery, simple ordering, and secure payments.";

  const stats =
    props.stats ?? [
      { value: "50+", label: "Partner Restaurants", icon: TrendingUp },
      { value: "25K+", label: "Happy Customers", icon: Users },
      { value: "4.9★", label: "Average Rating" },
      { value: "98%", label: "Satisfaction" },
    ];

  return (
    <section
      className="relative py-18 sm:py-20 bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950"
      aria-label="Testimonials"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(251,191,36,0.10),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_35%,rgba(249,115,22,0.10),transparent_60%)]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 mb-4 rounded-full border border-white/15 bg-white/10 px-4 py-2 backdrop-blur-xl">
            <Quote className="h-4 w-4 text-amber-300" />
            <span className="text-xs sm:text-sm font-semibold text-white/90 uppercase tracking-wide">
              Testimonials
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white">
            {heading}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-white/80 leading-relaxed">
            {subheading}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {testimonials.map((t, idx) => {
            const rating = clampRating(t.rating);
            return (
              <div
                key={`${t.author}-${idx}`}
                className={cn(
                  "group relative rounded-2xl border border-white/12 bg-white/6 backdrop-blur-xl p-7 sm:p-8",
                  "shadow-[0_30px_80px_-50px_rgba(0,0,0,0.8)]",
                  "transition-transform duration-300 hover:-translate-y-1 hover:bg-white/8"
                )}
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/0 via-white/0 to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />

                <div className="relative">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={`star-${idx}-${i}`}
                          className={cn(
                            "h-5 w-5",
                            i < rating ? "text-amber-400 fill-amber-400" : "text-white/20"
                          )}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-white/60">Verified</span>
                  </div>

                  <p className="mt-6 text-white/90 text-base sm:text-lg leading-relaxed italic">
                    “{t.content}”
                  </p>

                  <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-4">
                    <div className="relative h-14 w-14 overflow-hidden rounded-full border border-white/15 bg-white/10">
                      <Image
                        src={t.avatar}
                        alt={t.author}
                        fill
                        sizes="56px"
                        className="object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="text-white font-extrabold text-lg truncate">
                        {t.author}
                      </div>
                      <div className="text-white/70 text-sm truncate">{t.role}</div>
                      {t.company ? (
                        <div className="text-amber-300 font-semibold text-sm truncate">
                          {t.company}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {props.showStats !== false && (
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {stats.map((s, idx) => {
              const Icon = s.icon;
              return (
                <div
                  key={`${s.label}-${idx}`}
                  className="rounded-2xl border border-white/12 bg-white/6 backdrop-blur-xl p-5 sm:p-6"
                >
                  <div className="flex items-center gap-3">
                    {Icon ? (
                      <div className="h-10 w-10 rounded-2xl border border-white/12 bg-white/10 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-amber-300" />
                      </div>
                    ) : (
                      <div className="h-10 w-10 rounded-2xl border border-white/12 bg-white/10 flex items-center justify-center">
                        <span className="text-white/70 text-sm font-black">★</span>
                      </div>
                    )}
                    <div>
                      <div className="text-2xl sm:text-3xl font-extrabold text-white">
                        {s.value}
                      </div>
                      <div className="text-xs sm:text-sm text-white/70">
                        {s.label}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10 text-center text-xs text-white/55">
          Real testimonials. Real results. Built for Malawi.
        </div>
      </div>
    </section>
  );
}